import { useEffect, useState } from 'react';
import {
  EMPTY_TIMESHEET_FILTERS,
  TimesheetFilters,
  TimesheetsTable,
} from './components/TimesheetsTable';
import { useCurrentUserRole } from '../../hooks/useCurrentUserRole';
import { usePeopleOptions } from '../../hooks/usePeopleOptions';
import { hasRoleLevel } from '../../utils/roles';
import { notifyError } from '../../utils/notifyError';
import { saveBlobResponse } from '../../utils/saveBlob';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import DeclineModal from './components/DeclineModal';
import { useToast } from '../../utils/useToast';
import TimesheetsDetails from './components/TimesheetsDetails';
import { useGetTimesheets } from './hooks';
import timesheets from '../../network/timesheets';
import { Spinner } from '../../ui/Spinner';
import { Button } from '@tremor/react';
import { PlusIcon } from '@heroicons/react/24/solid';
import { CreateTimesheetModal } from './components/CreateTimesheetModal';

export function TimesheetsRoute() {
  const [isOpenDeclineModal, setIsOpenDeclineModal] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showTimesheetsDetails, setShowTimesheetsDetails] = useState(false);
  const [timesheetStatus, setTimesheetStatus] = useState({
    approved: 0,
    overtime: 0,
    pending: 0,
    rejected: 0,
    total: 0,
  });
  const [ currentTimesheet, setCurrentTimesheet ] = useState({
    id: '',
    approval_status: '',
    breaktime: '',
    date: '',
    employee: '',
    hours_worked: '',
    notes: '',
    user: {
      email: '',
      name: ''
    },
    work_time: {
      from: '',
      to: '',
    }
  });
  const [currentTimesheetId, setCurrentTimesheetId] = useState('');
  const [reason,setReason] = useState('');

  const { toast } = useToast();
  const [filters, setFilters] = useState<TimesheetFilters>(
    EMPTY_TIMESHEET_FILTERS,
  );
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const { level } = useCurrentUserRole();
  const isManager = hasRoleLevel(level, 'manager');
  const { people } = usePeopleOptions(isManager);

  const handleExport = async (type: 'csv' | 'xlsx') => {
    setExporting(type);
    try {
      const response = await timesheets.exportTimesheets({
        type,
        from: filters.from || undefined,
        to: filters.to || undefined,
        employees: filters.employees.length ? filters.employees : undefined,
        // The export takes one status; with several selected, export all.
        status:
          filters.statuses.length === 1 ? filters.statuses[0] : undefined,
      });
      saveBlobResponse(response, `timesheets.${type}`);
    } catch (error) {
      notifyError(error, 'Could not export timesheets.');
    } finally {
      setExporting(null);
    }
  };

  const { data: timesheetData, isLoading, refetch: refetchTimesheet } = useGetTimesheets();

  useEffect(() => {
    const getTimesheetstats = async () => {
      try {
        const {data} = await timesheets.getTimesheetsStatus();
        setTimesheetStatus(data)
      } catch (error) {
        notifyError(error, 'Could not load timesheet totals.');
      }
    }
    getTimesheetstats();
  }, [])

  const handleDeclineModal = (timesheetId: string) => {
    setIsOpenDeclineModal(true);
    setCurrentTimesheetId(timesheetId)
  };

  const handleDecline = async () => {
    if(currentTimesheetId?.length > 0) {
      try {
        const timesheet = {
          id: currentTimesheetId,
          decision: 'decline',
          comment: reason,
        }
        await timesheets.makeDecision(timesheet)
        refetchTimesheet();
        setIsOpenDeclineModal(false)
        toast({
          variant: 'success',
          title: `Decline Timesheets.
          ${timesheetData?.details?.employee?.name} will receive a connfirmaation notification.`,
        });
      } catch (error) {
        toast({
          variant: 'error',
          title: 'Something went wrong. Please try again later.'})
      }
    }
  };

  const handleApprove = async (timesheetData: any) => {
    try {
      const timesheet = {
        id: timesheetData?.id,
        decision: 'approve',
      }
      await timesheets.makeDecision(timesheet)
      refetchTimesheet();
      toast({
        variant: 'success',
        title: `Approved Timesheets.
        ${timesheetData?.user?.name} will receive a connfirmaation notification.`,
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Something went wrong. Please try again later.'})
    }
  };

  const handleTimesheetsDetails = async (id: string) => {
    try {

      const  { data }  = await timesheets.getTimesheetById(id);
      setCurrentTimesheet(data);
      setShowTimesheetsDetails(true);
      setCurrentTimesheetId(data?.id)
    } catch (error) {
      notifyError(error, 'Could not load the timesheet.');
    }
  }

  if(isLoading){
    return (
      <div className='h-[75vh] flex flex-col gap-3 justify-center items-center'>
        <Spinner size={10}/>
        <p className='font-semibold'>
          Loading...
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between border-b-2 border-[#E4E4E7] pb-4">
        <h1 className="text-3xl font-bold  text-tremor-content-strong">
          Timesheets
        </h1>
        <Button
          icon={PlusIcon}
          variant="primary"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add Entry
        </Button>
      </div>
      <div className="my-4 flex flex-row  gap-2">
        <div className='flex min-h-[50px] w-[33%] flex-col gap-1 rounded-lg p-4 bg-[#DBEAFE]'>
          <p className="text-sm text-[#71717A]">Total hours</p>
          <p className="text-2xl font-medium">{timesheetStatus.total}h</p>
        </div>
        <div className='flex min-h-[50px] w-[33%] flex-col gap-1 rounded-lg p-4 bg-[#FCE7F3]'>
          <p className="text-sm text-[#71717A]">Overtime</p>
          <p className="text-2xl font-medium">{timesheetStatus.overtime}h</p>
        </div>
        <div className='flex min-h-[50px] w-[33%] flex-col gap-1 rounded-lg p-4 bg-[#FFEDD5]'>
          <p className="text-sm text-[#71717A]">Pending Timesheets</p>
          <p className="text-2xl font-medium">{timesheetStatus.pending}h</p>
        </div>
        <div className='flex min-h-[50px] w-[33%] flex-col gap-1 rounded-lg p-4 bg-[#DCFCE7]'>
          <p className="text-sm text-[#71717A]">Approved Timesheets</p>
          <p className="text-2xl font-medium">{timesheetStatus.approved}h</p>
        </div>
        <div className='flex min-h-[50px] w-[33%] flex-col gap-1 rounded-lg p-4 bg-[#FEE2E2]'>
          <p className="text-sm text-[#71717A]">Declined Timesheets</p>
          <p className="text-2xl font-medium">{timesheetStatus.rejected}h</p>
        </div>
      </div>
      <div className="mt-5">
        <TimesheetsTable
          timesheetData={timesheetData}
          handleDeclineModal={handleDeclineModal}
          handleApprove={handleApprove}
          handleTimesheetsDetails={handleTimesheetsDetails}
          filters={filters}
          setFilters={setFilters}
          people={people}
          canFilterByEmployee={isManager}
          actions={
            isManager ? (
              <>
                <Button
                  icon={ArrowDownTrayIcon}
                  variant="secondary"
                  loading={exporting === 'csv'}
                  disabled={exporting !== null}
                  onClick={() => handleExport('csv')}
                >
                  Export CSV
                </Button>
                <Button
                  icon={ArrowDownTrayIcon}
                  variant="secondary"
                  loading={exporting === 'xlsx'}
                  disabled={exporting !== null}
                  onClick={() => handleExport('xlsx')}
                >
                  Export Excel
                </Button>
              </>
            ) : null
          }
        />
      </div>
      <DeclineModal 
        open={isOpenDeclineModal} 
        setOpen={setIsOpenDeclineModal}
        setReason={setReason}
        handleDecline={handleDecline}
         />
      <TimesheetsDetails 
        setOpen={setShowTimesheetsDetails}
        isOpen={showTimesheetsDetails}
        currentTimesheet={currentTimesheet}
        handleApprove={handleApprove}
        handleDecline={handleDecline}/>
      <CreateTimesheetModal
        isOpen={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        onSuccess={refetchTimesheet}
      />
    </>
  );
}
